import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import BLOG_POSTS from "../data/blogPosts";
import SEO from "../components/SEO";
import { getPageSeo, blogPath, breadcrumbSchema, blogSchema, itemListSchema } from "../lib/seo";

const Blog = () => {
  const navigate = useNavigate();

  // Sort posts most-recent first based on the "date" string (e.g. "March 28, 2023")
  const sortedPosts = useMemo(
    () =>
      [...BLOG_POSTS].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      ),
    []
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToCaseStudiesCTA = (e) => {
    e.preventDefault();
    navigate("/");
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById("case-studies-cta");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (attempt < 20) {
        setTimeout(() => tryScroll(attempt + 1), 50);
      }
    };
    setTimeout(() => tryScroll(0), 50);
  };

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/blog")}
        schema={[
          blogSchema(),
          itemListSchema("/blog", sortedPosts.map((p) => ({ path: blogPath(p.slug), name: p.title }))),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
        ]),
        ]}
      />
      {/* Page Header */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Link
            to="/"
            onClick={handleBackToCaseStudiesCTA}
            className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <p className="text-base md:text-lg font-semibold text-[#009688] uppercase tracking-wide mb-4">
            Blog
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Behind the Tap: Blog Posts
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Insights, stories, and best practices from the world of cashless RFID payments.
          </p>
        </div>
      </section>

      {/* Grid of Blog Posts */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sortedPosts.map((post) => (
              <Card
                key={post.slug}
                className="bg-[#37464F]/50 border-gray-800 hover:border-[#009688]/50 transition-all duration-300 overflow-hidden group flex flex-col"
                data-testid={`blog-tile-${post.slug}`}
              >
                <div className="relative h-56 overflow-hidden bg-[#101F27]">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101F27]/80 to-transparent"></div>
                </div>
                <CardContent className="p-6 flex flex-col flex-1">
                  <div className="text-xs text-[#009688] uppercase font-semibold tracking-wide mb-2">
                    {post.date}
                  </div>
                  <h2 className="text-xl font-bold text-white mb-3 leading-snug">
                    {post.title}
                  </h2>
                  <p className="text-gray-400 text-sm mb-6 flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-auto">
                    <Link
                      to={blogPath(post.slug)}
                      className="text-[#009688] text-sm font-semibold hover:underline inline-flex items-center group/cta"
                    >
                      Read Post
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
